import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select, { type SelectOption } from "../../../components/ui/Select";

interface Property {
	id: string;
	status?: string;
	monthly_rent?: number | string;
	[key: string]: any;
}

interface PropertySidebarProps {
	properties: Property[];
	selectedProperties: string[];
	onBulkAction: (action: string, selectedIds: string[]) => void;
}

interface StatCardProps {
	icon: string;
	title: string;
	value: string | number;
	subtitle?: string;
	color?: string;
}

const PropertySidebar: React.FC<PropertySidebarProps> = ({
	properties,
	selectedProperties,
	onBulkAction,
}) => {
	const [selectedBulkAction, setSelectedBulkAction] = useState<string>("");
	const [isMobileExpanded, setIsMobileExpanded] = useState<boolean>(false);

	// Safely calculate statistics with proper null checks
	const totalProperties = properties?.length || 0;

	// Calculate total units (default to 1 per property if not specified)
	// For now, we'll use 1 unit per property as a default
	const totalUnits = totalProperties; // Each property counts as 1 unit

	// Calculate occupied units (properties with status "occupied")
	const occupiedUnits =
		properties?.filter((prop) => prop?.status === "occupied").length || 0;

	// Calculate total monthly revenue from monthly_rent
	const totalRevenue =
		properties?.reduce((sum, prop) => {
			const rent = Number(prop?.monthly_rent) || 0;
			return sum + rent;
		}, 0) || 0;

	// Calculate average occupancy rate
	const averageOccupancy =
		totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0;

	// Safely filter properties by status
	const activeProperties =
		properties?.filter(
			(p) => p?.status === "available" || p?.status === "occupied",
		).length || 0;
	const maintenanceProperties =
		properties?.filter((p) => p?.status === "maintenance").length || 0;
	const vacantProperties =
		properties?.filter((p) => p?.status === "available").length || 0;

	const bulkActionOptions: SelectOption[] = [
		{ value: "", label: "Select Action" },
		{ value: "activate", label: "Mark as Active" },
		{ value: "maintenance", label: "Mark as Maintenance" },
		{ value: "vacant", label: "Mark as Vacant" },
		{ value: "export", label: "Export Selected" },
		{ value: "delete", label: "Delete Selected" },
	];

	const handleBulkActionSubmit = () => {
		if (selectedBulkAction && selectedProperties.length > 0) {
			onBulkAction(selectedBulkAction, selectedProperties);
			setSelectedBulkAction("");
		}
	};

	const StatCard: React.FC<StatCardProps> = ({
		icon,
		title,
		value,
		subtitle,
		color = "text-foreground",
	}) => (
		<div className="bg-muted/30 rounded-lg p-3 sm:p-4 overflow-hidden">
			<div className="flex items-center space-x-2 sm:space-x-3 gap-2">
				<div
					className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
						color === "text-success"
							? "bg-success/10 text-success"
							: color === "text-warning"
							? "bg-warning/10 text-warning"
							: color === "text-error"
							? "bg-error/10 text-error"
							: "bg-primary/10 text-primary"
					}`}
				>
					<Icon name={icon as any} size={18} className="sm:w-5 sm:h-5" />
				</div>
				<div className="flex-1 min-w-0">
					<p className="text-xs sm:text-sm text-muted-foreground truncate">
						{title}
					</p>
					<p className={`text-base sm:text-lg font-semibold ${color} truncate`}>
						{value}
					</p>
					{subtitle && (
						<p className="text-xs text-muted-foreground truncate">{subtitle}</p>
					)}
				</div>
			</div>
		</div>
	);

	return (
		<div className="space-y-4 sm:space-y-6">
			{/* Mobile Toggle Button */}
			<button
				onClick={() => setIsMobileExpanded(!isMobileExpanded)}
				className="xl:hidden w-full bg-card rounded-lg border border-border p-4 flex items-center justify-between text-foreground hover:bg-muted/50 transition-colors"
			>
				<div className="flex items-center gap-2">
					<Icon name="BarChart3" size={20} />
					<span className="font-semibold">Property Insights</span>
				</div>
				<Icon name={isMobileExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
			</button>

			{/* Sidebar Content - Hidden on mobile unless expanded */}
			<div
				className={`space-y-4 sm:space-y-6 ${
					isMobileExpanded ? "block" : "hidden xl:block"
				}`}
			>
				{/* Property Summary Statistics */}
				<div className="bg-card rounded-lg border border-border p-4 sm:p-6">
					<h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
						Property Overview
					</h3>

					<div className="space-y-3 sm:space-y-4">
						<StatCard
							icon="Building2"
							title="Total Properties"
							value={totalProperties.toString()}
							subtitle="Properties managed"
							color="text-primary"
						/>

						<StatCard
							icon="Home"
							title="Total Units"
							value={`${occupiedUnits}/${totalUnits}`}
							subtitle="Occupied/Total units"
							color="text-primary"
						/>

						<StatCard
							icon="TrendingUp"
							title="Monthly Revenue"
							value={
								totalRevenue > 0
									? `₦${totalRevenue.toLocaleString("en-NG", {
											maximumFractionDigits: 0,
									  })}`
									: "₦0"
							}
							subtitle="Total monthly income"
							color="text-success"
						/>

						<StatCard
							icon="BarChart3"
							title="Avg Occupancy"
							value={
								isNaN(averageOccupancy)
									? "0%"
									: `${averageOccupancy.toFixed(1)}%`
							}
							subtitle="Average occupancy rate"
							color={
								isNaN(averageOccupancy) || averageOccupancy >= 90
									? "text-success"
									: averageOccupancy >= 70
									? "text-warning"
									: "text-error"
							}
						/>
					</div>
				</div>

				{/* Status Distribution */}
				<div className="bg-card rounded-lg border border-border p-4 sm:p-6">
					<h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
						Status Distribution
					</h3>

					<div className="space-y-2 sm:space-y-3">
						<div className="flex items-center justify-between">
							<div className="flex items-center space-x-2">
								<div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-success rounded-full flex-shrink-0"></div>
								<span className="text-xs sm:text-sm text-foreground">
									Active
								</span>
							</div>
							<span className="text-xs sm:text-sm font-medium text-foreground">
								{activeProperties}
							</span>
						</div>

						<div className="flex items-center justify-between">
							<div className="flex items-center space-x-2">
								<div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-warning rounded-full flex-shrink-0"></div>
								<span className="text-xs sm:text-sm text-foreground">
									Maintenance
								</span>
							</div>
							<span className="text-xs sm:text-sm font-medium text-foreground">
								{maintenanceProperties}
							</span>
						</div>

						<div className="flex items-center justify-between">
							<div className="flex items-center space-x-2">
								<div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-muted rounded-full flex-shrink-0"></div>
								<span className="text-xs sm:text-sm text-foreground">
									Vacant
								</span>
							</div>
							<span className="text-xs sm:text-sm font-medium text-foreground">
								{vacantProperties}
							</span>
						</div>
					</div>

					{/* Status Chart */}
					<div className="mt-3 sm:mt-4 h-2 bg-muted rounded-full overflow-hidden">
						<div className="h-full flex">
							{totalProperties > 0 ? (
								<>
									<div
										className="bg-success"
										style={{
											width: `${Math.min(
												(activeProperties / totalProperties) * 100,
												100,
											)}%`,
										}}
									/>
									<div
										className="bg-warning"
										style={{
											width: `${Math.min(
												(maintenanceProperties / totalProperties) * 100,
												100,
											)}%`,
										}}
									/>
									<div
										className="bg-muted-foreground"
										style={{
											width: `${Math.min(
												(vacantProperties / totalProperties) * 100,
												100,
											)}%`,
										}}
									/>
								</>
							) : (
								<div className="bg-muted-foreground w-full" />
							)}
						</div>
					</div>
				</div>

				{/* Bulk Actions */}
				{selectedProperties.length > 0 && (
					<div className="bg-card rounded-lg border border-border p-4 sm:p-6">
						<h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
							Bulk Actions ({selectedProperties.length} selected)
						</h3>

						<div className="space-y-2 sm:space-y-3">
							<Select
								value={selectedBulkAction}
								options={bulkActionOptions}
								onValueChange={(value) =>
									setSelectedBulkAction(value as string)
								}
								placeholder="Select Action"
							/>

							<Button
								variant="default"
								onClick={handleBulkActionSubmit}
								disabled={!selectedBulkAction}
								fullWidth
							>
								Apply Action
							</Button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default PropertySidebar;
